import React from 'react';
import { useNavigate } from 'react-router-dom';
import projectData from './projectData.ts';
import ResumePreview from './ResumePreview';

const RESUME_URL = '/files/Joe Skubic Resume 6-22-26.pdf';

const Home = () => {
  const navigate = useNavigate();

  const openProjectPage = (projectId, project) => {
    navigate(`/project/${projectId}`);
  };

  const scrollToProjects = () => {
    const element = document.getElementById('projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <main>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-container">
          <div className="hero-image-container">
            <div className="hero-image">
              <img src="/images/personal/joe-portrait.jpg" alt="Joe Skubic" />
            </div>
          </div>
          
          <div className="hero-content">
            <h1 className="hero-title">
              Hey! I'm{" "}
              <span className="hero-highlight">Joe</span>
            </h1>
            <p className="hero-subtitle">
              "The only thing that's impossible is impossibility." - Phineas Flynn
            </p>

            {/* Scroll Down Indicator */}
            <div className="scroll-indicator" onClick={scrollToProjects}>
              <span className="scroll-text">See my work</span>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="scroll-arrow"
              >
                <path
                  d="M7 10L12 15L17 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <h2 className="section-title">About Me</h2>
          <div className="about-content">
            <div className="about-text">
              <p>
                I'm a Mechanical Engineering student at Tufts University (Class of 2028) and a project lead on the Tufts Electric Racing Team. I spend most of my time designing, machining, and leading hands-on builds for our Formula SAE Electric car. I also make custom watches and aim to bring together precision engineering with thoughtful design.
              </p>
              <ResumePreview file={RESUME_URL} />
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="projects">
        <div className="container">
          <div className="projects-header">
            <h2 className="section-title">Projects</h2>
            <p className="projects-subtitle">A showcase of my engineering projects</p>
          </div>
          
          <div className="projects-grid">
            {Object.entries(projectData)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([projectId, project]) => (
              <div key={projectId} className="project-card" onClick={() => openProjectPage(projectId, project)}>
                <div className="project-image">
                  <img src={project.mainImage} alt={project.title} />
                  {project.inProgress && (
                    <span className="status-badge in-progress">In Progress</span>
                  )}
                  <div className="project-overlay">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="external-link-icon">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="15,3 21,3 21,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p>{project.snippet}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title">Contact</h2>
          <div className="contact-content">
            <p>
              I'm always interested in new opportunities, collaborations, and interesting projects. 
              Feel free to reach out!
            </p>
            <div className="contact-buttons">
              <a href="mailto:joe@skubic.com" className="btn btn-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="email-icon">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Send me an email
              </a>
              <a href="https://www.linkedin.com/in/joe-skubic/" target="_blank" rel="noopener noreferrer" className="btn btn-linkedin">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="linkedin-icon">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>© 2025 Joe Skubic — Portfolio</p>
        </div>
      </footer>
    </main>
  );
};

export default Home;
